import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import NewMfaSession from 'client/js/new_mfa_session';
import SixDigitCodeInput from 'client/js/components/six_digit_code_input';
import { BooleanField, PasswordField, StringField } from 'client/js/util/form_utils';

const csrfToken = () => {
  if (typeof document === 'undefined') return null;

  return document.querySelector('meta[name="csrf-token"]')?.content;
};

const setCsrfToken = (token) => {
  if (typeof document === 'undefined' || !token) return;

  document.querySelector('meta[name="csrf-token"]')?.setAttribute('content', token);
};

const matomoPush = (args) => {
  if (typeof window === 'undefined') return;

  window._paq = window._paq || [];
  window._paq.push(args);
};

const trackWizardEvent = (action, offerCode, leadId) => {
  matomoPush(['trackEvent', 'hosting_trial_wizard', action, offerCode || 'unknown', leadId || 0]);
};

const trackWizardPage = (step) => {
  matomoPush(['setCustomUrl', `/webhosting/kostenlos-testen/${step}`]);
  matomoPush(['setDocumentTitle', `Webhosting-Test ${step}`]);
  matomoPush(['trackPageView']);
};

const queryTrackingParams = () => {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  const result = {};

  ['utm_source', 'utm_medium', 'utm_campaign'].forEach((key) => {
    if (params.get(key)) result[key] = params.get(key);
  });

  return result;
};

const postJSON = async (url, payload) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify(payload)
  });

  let data = {};

  try {
    data = await response.json();
  } catch (_error) {
    data = {
      message: response.status === 429
        ? 'Zu viele Anfragen. Bitte warte kurz und versuche es dann erneut.'
        : 'Die Anfrage konnte gerade nicht verarbeitet werden. Bitte versuche es erneut.'
    };
  }

  if (!response.ok) {
    const error = new Error(data.message || 'Die Anfrage konnte nicht verarbeitet werden.');
    error.data = data;
    error.status = response.status;
    throw error;
  }

  return data;
};

class WebhostingTrialWizard extends React.Component {
  constructor(props) {
    super(props);

    const offerCode = props.lead?.offer_code || props.initial_offer_code || props.offers[0]?.offer_code || '';
    const savedStep = ['signup', 'activation', 'confirm', 'success', 'blocked'].includes(props.lead?.current_step) ? props.lead.current_step : null;
    const initialStep = props.logged_in && props.user_confirmed && savedStep === 'activation'
      ? 'confirm'
      : (savedStep || (props.lead || props.logged_in ? 'signup' : 'email'));
    const hosttestReviews = props.hosttest_reviews || {};
    const accountCreated = ['activation', 'confirm', 'success'].includes(initialStep) || props.lead?.status === 'trial_started';

    this.state = {
      step: initialStep,
      email: props.lead?.email || props.user_email || '',
      offerCode: offerCode,
      leadId: props.lead?.id || null,
      leadToken: null,
      accountCreated: accountCreated,
      loggedIn: props.logged_in,
      userConfirmed: props.user_confirmed,
      authMode: 'signup',
      authTransitionDirection: 'none',
      loginUsername: '',
      loginPassword: '',
      loginRemember: false,
      loginVerificationEmail: null,
      loginVerificationCode: '',
      loginVerificationError: null,
      username: '',
      password: '',
      tosAccepted: false,
      subscribe: false,
      recaptchaResponse: null,
      activationCode: '',
      activationConfirmed: false,
      activationError: null,
      trialSetupError: null,
      loading: props.logged_in && !props.lead,
      error: null,
      result: null,
      captchaMounted: false,
      hosttestReviews: hosttestReviews.reviews || [],
      hosttestReviewIndex: 0
    };
  }

  componentDidMount() {
    this.setState({ captchaMounted: true });
    trackWizardEvent('wizard_opened', this.state.offerCode, this.state.leadId);
    trackWizardPage(this.state.step);
    this.startHosttestCarousel(this.state.hosttestReviews);

    if (this.isLoggedIn() && !this.props.lead) {
      this.submitLead(null);
    }
  }

  componentDidUpdate(_previousProps, previousState) {
    if (previousState.step !== this.state.step) {
      trackWizardPage(this.state.step);
    }
  }

  componentWillUnmount() {
    if (this.hosttestCarouselInterval) clearInterval(this.hosttestCarouselInterval);
  }

  startHosttestCarousel(reviews) {
    if (this.hosttestCarouselInterval) clearInterval(this.hosttestCarouselInterval);
    if (reviews.length <= 1) return;

    this.hosttestCarouselInterval = setInterval(() => {
      this.setState((state) => ({
        hosttestReviewIndex: (state.hosttestReviewIndex + 1) % state.hosttestReviews.length
      }));
    }, 6500);
  }

  selectedOffer() {
    return this.props.offers.find((offer) => offer.offer_code === this.state.offerCode) || this.props.offers[0];
  }

  isLoggedIn() {
    return this.state.loggedIn;
  }

  isUserConfirmed() {
    return this.state.userConfirmed;
  }

  setAuthMode(authMode, authTransitionDirection = 'forward') {
    this.setState({ authMode: authMode, authTransitionDirection: authTransitionDirection, error: null });
  }

  resetAuthentication = async (authMode, restartLead = false) => {
    this.setState({ loading: true, error: null });

    try {
      if (this.isLoggedIn()) {
        const data = await postJSON(this.props.sign_out_url, {});
        setCsrfToken(data.csrf_token);
      }

      this.activationVerificationInFlight = false;
      this.setState({
        loading: false,
        loggedIn: false,
        userConfirmed: false,
        email: restartLead ? '' : this.state.email,
        leadId: restartLead ? null : this.state.leadId,
        leadToken: restartLead ? null : this.state.leadToken,
        accountCreated: restartLead ? false : this.state.accountCreated,
        authMode: authMode,
        authTransitionDirection: authMode === 'login' ? 'forward' : 'back',
        loginUsername: '',
        loginPassword: '',
        loginRemember: false,
        loginVerificationEmail: null,
        loginVerificationCode: '',
        loginVerificationError: null,
        username: '',
        password: '',
        tosAccepted: false,
        recaptchaResponse: null,
        activationCode: '',
        activationConfirmed: false,
        activationError: null,
        trialSetupError: null,
        result: null,
        step: restartLead ? 'email' : 'signup'
      });
    } catch (error) {
      this.setState({ loading: false, error: error.data || { message: error.message } });
    }
  }

  goToEmailStep = () => {
    if (this.state.loading || this.state.step === 'email' || this.emailStepLocked()) return;

    this.resetAuthentication('signup', true);
  }

  emailStepLocked() {
    return this.state.accountCreated || this.isLoggedIn();
  }

  fieldErrors() {
    return this.state.error?.errors || {};
  }

  requestErrorMessage(error, fallback) {
    if (error.status === 429) return 'Zu viele Anfragen. Bitte warte kurz und versuche es dann erneut.';
    if (!error.data || error.status >= 500) return fallback;

    return error.data.message || fallback;
  }

  firstErrorFor(field) {
    const errors = this.fieldErrors()[field];

    return errors && errors.length ? errors.join(', ') : null;
  }

  setupStepIndex() {
    if (this.state.step === 'signup') return 1;
    if (this.state.step === 'activation') return 2;
    if (this.state.step === 'confirm') return 2;
    if (this.state.step === 'success') return 3;
    if (this.state.step === 'blocked') return 2;

    return 0;
  }

  submitLead = async (event) => {
    if (event) event.preventDefault();

    this.setState({ loading: true, error: null });

    try {
      const data = await postJSON(this.props.create_lead_url, {
        email: this.state.email,
        offer_code: this.state.offerCode,
        landing_url: typeof window !== 'undefined' ? window.location.href : null,
        ...queryTrackingParams()
      });

      const lead = data.object;
      trackWizardEvent('email_submitted', lead.offer_code, lead.id);

      this.setState({
        loading: false,
        leadId: lead.id,
        leadToken: lead.lead_token,
        email: lead.email,
        offerCode: lead.offer_code,
        accountCreated: false,
        activationCode: '',
        activationConfirmed: false,
        activationError: null,
        trialSetupError: null,
        result: null,
        step: 'signup'
      });
    } catch (error) {
      this.setState({ loading: false, error: error.data || { message: error.message } });
    }
  }

  submitSignup = async (event, leadToken = this.state.leadToken) => {
    if (event) event.preventDefault();

    if (!this.isLoggedIn() && !this.state.tosAccepted) {
      return this.setState({ error: { errors: { tos_accepted: ['muss akzeptiert werden'] } } });
    }

    this.setState({ loading: true, error: null, trialSetupError: null });
    trackWizardEvent('signup_submitted', this.state.offerCode, this.state.leadId);

    try {
      const payload = { lead_token: leadToken };

      if (!this.isLoggedIn()) {
        payload.signup = {
          username: this.state.username,
          email: this.state.email,
          password: this.state.password,
          tos_accepted: this.state.tosAccepted ? '1' : '0',
          subscribe: this.state.subscribe ? '1' : '0',
          recaptcha_response: this.state.recaptchaResponse
        };
      }

      const data = await postJSON(this.props.signup_url, payload);
      const result = data.object;

      if (result.trial_started) {
        trackWizardEvent('trial_started', result.offer_code, this.state.leadId);
      } else if (result.account_created && result.activation_required) {
        trackWizardEvent('account_created', result.offer_code, this.state.leadId);
      } else {
        trackWizardEvent('trial_blocked', result.offer_code, this.state.leadId);
      }

      const activationRequired = result.account_created && result.activation_required;

      this.setState({
        loading: false,
        result: result,
        accountCreated: this.state.accountCreated || result.account_created || result.trial_started,
        activationConfirmed: activationRequired ? false : this.state.activationConfirmed,
        loggedIn: this.state.loggedIn || result.account_created,
        step: activationRequired ? 'activation' : (result.trial_started ? 'success' : 'blocked')
      });
    } catch (error) {
      const message = this.requestErrorMessage(error, 'Das Testpaket konnte gerade nicht eingerichtet werden. Bitte versuche es erneut.');

      if (this.state.activationConfirmed || this.state.step === 'activation' || this.state.step === 'confirm') {
        this.activationVerificationInFlight = false;
        return this.setState({
          loading: false,
          trialSetupError: message
        });
      }

      this.setState({ loading: false, error: error.data || { message: message } });
    }
  }

  submitLogin = async (event) => {
    event.preventDefault();

    this.setState({ loading: true, error: null, loginVerificationError: null });
    trackWizardEvent('login_submitted', this.state.offerCode, this.state.leadId);

    try {
      const data = await postJSON('/login', {
        user_session: {
          username: this.state.loginUsername,
          password: this.state.loginPassword,
          remember_me: this.state.loginRemember ? '1' : '0'
        }
      });

      const result = data.object || {};

      if (result.device_verification_required) {
        return this.setState({
          loading: false,
          authMode: 'login_verification',
          authTransitionDirection: 'forward',
          loginVerificationEmail: result.email,
          loginVerificationCode: ''
        });
      }

      if (result.mfa_required) {
        return this.setState({
          loading: false,
          authMode: 'login_mfa',
          authTransitionDirection: 'forward'
        });
      }

      this.setState({ loading: false, loggedIn: true, step: 'confirm' });
    } catch (error) {
      this.setState({ loading: false, error: error.data || { message: error.message } });
    }
  }

  verifyLoginCode = async (code = this.state.loginVerificationCode) => {
    if (code.length !== 6 || this.state.loading) return;

    this.setState({ loading: true, loginVerificationError: null });

    try {
      const response = await fetch('/verify-login-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ code: code })
      });
      const data = await response.json();

      if (response.ok && data.success) {
        return this.setState({ loading: false, loggedIn: true, step: 'confirm' });
      }

      this.setState({
        loading: false,
        loginVerificationCode: '',
        loginVerificationError: data.message || 'Der Code konnte nicht geprüft werden.'
      });
    } catch (error) {
      this.setState({
        loading: false,
        loginVerificationCode: '',
        loginVerificationError: error.message || 'Der Code konnte nicht geprüft werden.'
      });
    }
  }

  completeInlineMfa = () => {
    this.setState({ loggedIn: true, step: 'confirm' });
  }

  changeActivationCode = (code) => {
    this.setState({ activationCode: code, activationError: null });
  }

  verifyActivationCode = async (code = this.state.activationCode) => {
    if (code.length !== 6 || this.state.loading || this.activationVerificationInFlight) return;

    this.activationVerificationInFlight = true;
    this.setState({ loading: true, activationError: null });

    try {
      const data = await postJSON(this.props.activation_url, { code: code });

      if (data.status === 'ok') {
        trackWizardEvent('activation_completed', this.state.offerCode, this.state.leadId);
        this.setState({
          activationCode: '',
          activationConfirmed: true,
          loading: false,
          loggedIn: true,
          userConfirmed: true,
          trialSetupError: null
        }, () => {
          this.activationVerificationInFlight = false;

          if (this.state.result?.trial_started) {
            this.setState({ step: 'success' });
          } else {
            this.setState({ step: 'confirm' });
          }
        });
      } else {
        this.activationVerificationInFlight = false;
        this.setState({ loading: false, activationError: data.message || 'Der Code konnte nicht geprüft werden.' });
      }
    } catch (error) {
      this.activationVerificationInFlight = false;
      this.setState({
        loading: false,
        activationCode: '',
        activationError: this.requestErrorMessage(error, 'Der Code konnte nicht geprüft werden. Bitte versuche es erneut.')
      });
    }
  }

  renderOfferSummary() {
    const offer = this.selectedOffer();
    if (!offer) return null;

    const offerPriceSubline = offer.first_interval
      ? `für ${offer.first_interval_months} ${offer.first_interval_months === 1 ? 'Monat' : 'Monate'}`
      : 'nach dem Test';
    const taxValue = offer.first_payment_tax_amount || offer.monthly_tax_amount || 'Reverse Charge';

    return (
      <div className="trial-offer-card">
        <div className="trial-offer-eyebrow">Dein Testpaket</div>
        <div className="trial-offer-name">{offer.name}</div>
        <div className="trial-test-price">
          <span>14 Tage testen</span>
          <strong>0,00 €</strong>
        </div>
        <div className="trial-offer-price-lines">
          <div>
            <span>Angebotspreis <em>{offerPriceSubline}</em></span>
            <strong>{offer.first_interval ? offer.first_interval_price : offer.regular_price}<small>/Monat</small></strong>
          </div>
          {offer.first_interval && (
            <div>
              <span>Danach regulär</span>
              <strong>{offer.regular_price}<small>/Monat</small></strong>
            </div>
          )}
          <div>
            <span>Einrichtung</span>
            <strong>{offer.setup_price_free ? 'kostenlos' : offer.setup_price}</strong>
          </div>
          <div>
            <span>Laufzeit</span>
            <strong>{offer.contract_period_label || `${offer.contract_period} Monate`}</strong>
          </div>
          <div>
            <span>Abrechnung</span>
            <strong>{offer.billing_cycle_label || `alle ${offer.billing_cycle} Monate`}</strong>
          </div>
          <div>
            <span>Erste Abrechnung nach dem Test</span>
            <strong>{offer.first_payment_amount}</strong>
          </div>
          <div>
            <span>Enthaltene USt.</span>
            <strong>{taxValue}</strong>
          </div>
        </div>
        <div className="trial-offer-grid">
          <div><strong>{offer.disk_space_gb} GB</strong><span>Webspace</span></div>
          <div><strong>{offer.mysql_space_gb || 0} GB</strong><span>MySQL</span></div>
          <div><strong>{offer.email_space_gb || 0} GB</strong><span>E-Mail</span></div>
        </div>
      </div>
    );
  }

  renderOfferFootnote() {
    const offer = this.selectedOffer();
    if (!offer?.gross_price_footnote) return null;

    return <p className="trial-offer-footnote">* {offer.gross_price_footnote}</p>;
  }

  renderError() {
    if (!this.state.error?.message && !this.state.error?.errors?.base) return null;

    return (
      <div className="alert alert-danger">
        {this.state.error.message || this.state.error.errors.base.join(', ')}
      </div>
    );
  }

  renderFieldError(field) {
    const message = this.firstErrorFor(field);
    if (!message) return null;

    return <div className="invalid-feedback d-block">{message}</div>;
  }

  renderHosttestReviews() {
    const { hosttestReviews, hosttestReviewIndex } = this.state;

    if (!hosttestReviews.length) return null;

    const review = hosttestReviews[hosttestReviewIndex % hosttestReviews.length];

    return (
      <div className="trial-hosttest-panel">
        <div className="trial-hosttest-heading">Das sagen unsere Kunden:</div>
        <blockquote key={review.id} className="trial-hosttest-review">
          {review.title && <strong>{review.title}</strong>}
          <p>{review.text}</p>
          <footer className="trial-hosttest-review-meta">
            <span>{review.author || 'Kunde'}</span>
            <span>{review.date}</span>
          </footer>
        </blockquote>
      </div>
    );
  }

  renderTrustPanel() {
    return (
      <div className="trial-intro-copy">
        <div className="trial-kicker">lima-city Webhosting</div>
        <h1>Starte Deinen Test ohne Umwege.</h1>
        <div className="trial-trust-list">
          <div>
            <strong>Keine Zahlungsdaten</strong>
            <span>Der Test startet kostenlos und endet ohne automatische Buchung.</span>
          </div>
          <div>
            <strong>Direkt eingerichtet</strong>
            <span>Nach dem Signup legen wir Dein gewähltes Paket sofort an.</span>
          </div>
          <div>
            <strong>Made in Germany</strong>
            <span>Hosting auf lima-city Infrastruktur mit Support aus Bremen.</span>
          </div>
        </div>
        {this.renderHosttestReviews()}
      </div>
    );
  }

  renderWizardProgress() {
    const steps = [
      ['E-Mail', 0],
      ['Account', 1],
      ['Bestätigung', 2],
      ['Fertig', 3]
    ];
    const current = this.setupStepIndex();

    return (
      <div className="trial-progress">
        {steps.map(([label, index]) => (
          <button
            key={label}
            type="button"
            className={`trial-progress-step ${index <= current ? 'active' : ''}`}
            disabled={index !== 0 || this.state.loading || this.state.step === 'email' || this.emailStepLocked()}
            onClick={this.goToEmailStep}
          >
            <span>{index + 1}</span>
            {label}
          </button>
        ))}
      </div>
    );
  }

  renderWizardShell(content) {
    return (
      <div className="trial-setup-shell">
        <div className="trial-setup-heading">
          <div>
            <h1>Webhosting-Test einrichten</h1>
          </div>
          {this.renderWizardProgress()}
        </div>
        <div className="trial-setup-grid">
          <aside className="trial-package-column">
            {this.renderOfferSummary()}
            {this.renderOfferFootnote()}
          </aside>
          <section
            key={this.state.authMode}
            className={`trial-dialog-card trial-dialog-swipe trial-dialog-swipe-${this.state.authTransitionDirection}`}
          >
            {content}
          </section>
        </div>
      </div>
    );
  }

  renderEmailStep() {
    return (
      <div className="trial-intro-shell">
        {this.renderTrustPanel()}
        <div className="trial-email-column">
          {this.renderWizardProgress()}
          <form className="trial-dialog-card trial-email-card" onSubmit={this.submitLead}>
            <div className="trial-kicker">Schritt 1</div>
            <h2>Mit Deiner E-Mail starten</h2>
            <p>Wir speichern Deine E-Mail-Adresse, damit Du den begonnenen Test später zuverlässig fortsetzen kannst.</p>

            {this.renderError()}

            <div className="form-group">
              <label htmlFor="trial_email">E-Mail-Adresse</label>
              <input
                id="trial_email"
                type="email"
                required
                className="form-control form-control-lg"
                autoComplete="email"
                placeholder="du@example.de"
                value={this.state.email}
                onChange={(event) => this.setState({ email: event.target.value })}
                disabled={this.state.loading}
              />
              {this.renderFieldError('email')}
            </div>

            <button className="btn btn-primary btn-lg btn-block" disabled={this.state.loading}>
              {this.state.loading ? 'bitte warten...' : this.isLoggedIn() ? 'Test starten' : 'weiter zum Setup'}
            </button>
            <small>Kein Spam. Nur ein Fortsetzen-Link, falls Du abbrechen musst.</small>
          </form>
        </div>
      </div>
    );
  }

  renderSignupStep() {
    if (this.isLoggedIn()) {
      return this.isUserConfirmed() ? this.renderConfirmStep() : this.renderActivationStep();
    }

    if (this.state.authMode === 'login') return this.renderLoginStep();
    if (this.state.authMode === 'login_verification') return this.renderLoginVerificationStep();
    if (this.state.authMode === 'login_mfa') return this.renderLoginMfaStep();

    return this.renderWizardShell(
      <form onSubmit={this.submitSignup}>
        <div className="trial-kicker">Account</div>
        <h2>Account anlegen</h2>
        <p>Ohne Zahlungsdaten, ohne automatische Verlängerung.</p>
        {this.renderError()}

        {!this.state.email && (
          <div className="form-group">
            <label htmlFor="trial_signup_email">E-Mail-Adresse *</label>
            <input
              id="trial_signup_email"
              type="email"
              required
              className="form-control"
              autoComplete="email"
              value={this.state.email}
              onChange={(event) => this.setState({ email: event.target.value })}
              disabled={this.state.loading}
            />
            {this.renderFieldError('email')}
          </div>
        )}

        <StringField
          id="trial_username"
          name="username"
          required
          label="Benutzername"
          autoComplete="username"
          value={this.state.username}
          onChange={(event) => this.setState({ username: event.target.value.toLowerCase() })}
          errors={this.fieldErrors().username || []}
          disabled={this.state.loading}
        />

        <PasswordField
          id="trial_password"
          name="password"
          required
          label="Passwort"
          value={this.state.password}
          onChange={(event) => this.setState({ password: event.target.value })}
          errors={this.fieldErrors().password || []}
          disabled={this.state.loading}
        />

        <BooleanField
          id="trial_tos"
          name="tos_accepted"
          required
          checked={this.state.tosAccepted}
          onChange={(event) => this.setState({ tosAccepted: event.target.checked })}
          disabled={this.state.loading}
          errors={this.fieldErrors().tos_accepted || []}
          label={<span>Ich habe die <a href="/AGB.pdf" target="_blank" rel="noopener noreferrer">allgemeinen Geschäftsbedingungen</a> gelesen, verstanden und akzeptiere sie.</span>}
        />

        <BooleanField
          id="trial_subscribe"
          name="subscribe"
          checked={this.state.subscribe}
          onChange={(event) => this.setState({ subscribe: event.target.checked })}
          disabled={this.state.loading}
          label="Ich möchte den Newsletter und Angebote erhalten"
        />

        {this.props.show_captcha && this.state.captchaMounted && (
          <div className="mb-3">
            <div className="signup-captcha-text">Bitte zeige uns, dass Du ein Mensch bist:</div>
            <ReCAPTCHA
              sitekey={this.props.recaptcha_site_key}
              onChange={(value) => this.setState({ recaptchaResponse: value })}
            />
            {this.renderFieldError('captcha')}
          </div>
        )}

        <button className="btn btn-primary btn-lg btn-block" disabled={this.state.loading}>
          {this.state.loading ? 'Test wird eingerichtet...' : 'Account anlegen und Test starten'}
        </button>
        <div className="trial-login-switch">
          Du hast schon einen Account?{' '}
          <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => this.setAuthMode('login', 'forward')}>
            Einloggen und hier fortsetzen
          </button>
        </div>
      </form>
    );
  }

  renderLoginStep() {
    return this.renderWizardShell(
      <form onSubmit={this.submitLogin}>
        <div className="trial-kicker">Login</div>
        <h2>Einloggen</h2>
        <p>Logge Dich ein, dann richten wir das Testpaket direkt auf Deinem bestehenden Account ein.</p>
        {this.renderError()}

        <div className="form-group">
          <label htmlFor="trial_login_username">Benutzername</label>
          <input
            id="trial_login_username"
            type="text"
            required
            className="form-control"
            autoComplete="username"
            value={this.state.loginUsername}
            onChange={(event) => this.setState({ loginUsername: event.target.value })}
            disabled={this.state.loading}
          />
          {this.renderFieldError('username')}
        </div>

        <div className="form-group">
          <label htmlFor="trial_login_password">Passwort</label>
          <input
            id="trial_login_password"
            type="password"
            required
            className="form-control"
            autoComplete="current-password"
            value={this.state.loginPassword}
            onChange={(event) => this.setState({ loginPassword: event.target.value })}
            disabled={this.state.loading}
          />
          {this.renderFieldError('password')}
        </div>

        <div className="custom-control custom-checkbox mb-3">
          <input
            id="trial_login_remember"
            type="checkbox"
            className="custom-control-input"
            checked={this.state.loginRemember}
            onChange={(event) => this.setState({ loginRemember: event.target.checked })}
            disabled={this.state.loading}
          />
          <label className="custom-control-label" htmlFor="trial_login_remember">
            an diesem Computer vier Wochen eingeloggt bleiben
          </label>
        </div>

        <button className="btn btn-primary btn-lg btn-block" disabled={this.state.loading}>
          {this.state.loading ? 'Login wird geprüft...' : 'Einloggen und Test starten'}
        </button>
        <div className="trial-login-switch">
          Noch kein Account?{' '}
          <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => this.setAuthMode('signup', 'back')}>
            Account anlegen
          </button>
        </div>
      </form>
    );
  }

  renderLoginVerificationStep() {
    return this.renderWizardShell(
      <div>
        <div className="trial-kicker">Sicherheitsprüfung</div>
        <h2>Login bestätigen</h2>
        <p>Wir haben einen 6-stelligen Code an <strong>{this.state.loginVerificationEmail || 'Deine E-Mail-Adresse'}</strong> gesendet.</p>
        {this.state.loginVerificationError && <div className="alert alert-danger">{this.state.loginVerificationError}</div>}
        <SixDigitCodeInput
          value={this.state.loginVerificationCode}
          onChange={(code) => this.setState({ loginVerificationCode: code, loginVerificationError: null })}
          onComplete={this.verifyLoginCode}
          disabled={this.state.loading}
          className="d-flex justify-content-center mb-3"
        />
        <button className="btn btn-primary btn-lg btn-block" disabled={this.state.loginVerificationCode.length !== 6 || this.state.loading} onClick={() => this.verifyLoginCode()}>
          {this.state.loading ? 'Code wird geprüft...' : 'Login bestätigen und Test starten'}
        </button>
      </div>
    );
  }

  renderLoginMfaStep() {
    return this.renderWizardShell(
      <div>
        <div className="trial-kicker">Zwei-Faktor-Authentifizierung</div>
        <h2>Login bestätigen</h2>
        <NewMfaSession inline onSuccess={this.completeInlineMfa} />
      </div>
    );
  }

  renderActivationStep() {
    const result = this.state.result || {};

    return this.renderWizardShell(
      <div>
        <div className="trial-kicker">Bestätigung</div>
        <h2>E-Mail bestätigen</h2>
        {result.trial_started && (
          <div className="alert alert-success">
            Dein Webhosting-Test wurde gestartet.
          </div>
        )}
        <p>Wir haben einen 6-stelligen Code an <strong>{result.email || this.state.email}</strong> gesendet. Bitte gib ihn hier ein, um Deinen Account zu aktivieren.</p>

        {this.state.activationError && <div className="alert alert-danger">{this.state.activationError}</div>}

        <form onSubmit={(event) => this.verifyActivationCode(event.preventDefault() || this.state.activationCode)}>
          <SixDigitCodeInput
            value={this.state.activationCode}
            onChange={this.changeActivationCode}
            onComplete={this.verifyActivationCode}
            disabled={this.state.loading}
            className="d-flex justify-content-center mb-3"
          />

          <button className="btn btn-primary btn-lg btn-block" disabled={this.state.activationCode.length !== 6 || this.state.loading}>
            {this.state.loading ? 'Code wird geprüft...' : 'Account aktivieren'}
          </button>
        </form>
      </div>
    );
  }

  renderConfirmStep() {
    const offer = this.selectedOffer();

    return this.renderWizardShell(
      <div>
        <div className="trial-kicker">Bereit zum Start</div>
        <h2>Webhosting-Test starten</h2>
        <p>Dein Account ist bestätigt. Wenn Du fortfährst, richten wir das Paket <strong>{offer?.name || 'Webhosting'}</strong> jetzt als kostenlosen 14-Tage-Test für Dich ein.</p>
        <p>Ohne Zahlungsdaten, ohne automatische Verlängerung.</p>
        {this.state.trialSetupError && <div className="alert alert-danger">{this.state.trialSetupError}</div>}
        {this.renderError()}
        <button className="btn btn-primary btn-lg btn-block" disabled={this.state.loading} onClick={(event) => this.submitSignup(event)}>
          {this.state.loading ? 'Testpaket wird eingerichtet...' : 'Testpaket jetzt einrichten'}
        </button>
      </div>
    );
  }

  renderSuccessStep() {
    return this.renderWizardShell(
      <div>
        <div className="trial-kicker">Fertig</div>
        <h2>Test gestartet</h2>
        <div className="alert alert-success">
          Dein Webhosting-Test ist eingerichtet.
        </div>
        <p>Du bist fertig. Ab jetzt geht es in der Verwaltung weiter.</p>
        <a href="/usercp" className="btn btn-primary btn-lg btn-block mb-2">zur Verwaltung</a>
        <a href="/usercp/websites/lima-subdomain/new" className="btn btn-light btn-lg btn-block mb-2">erste Webseite anlegen</a>
        <a href="/domain-check" className="btn btn-light btn-lg btn-block mb-2">Domain suchen</a>
      </div>
    );
  }

  renderBlockedStep() {
    const result = this.state.result || {};

    return this.renderWizardShell(
      <div>
        <div className="trial-kicker">Prüfung</div>
        <h2>Test nicht verfügbar</h2>
        <div className="alert alert-warning">
          {result.message || 'Der kostenlose Test ist für diesen Account derzeit nicht verfügbar.'}
        </div>
        <p>Du kannst ein Webhosting-Paket direkt buchen oder Dich an den Support wenden.</p>
        {!this.emailStepLocked() && (
          <button type="button" className="btn btn-primary btn-lg btn-block mb-2" disabled={this.state.loading} onClick={() => this.resetAuthentication('signup', true)}>
            {this.state.loading ? 'bitte warten...' : 'neuen Account anlegen'}
          </button>
        )}
        <button type="button" className="btn btn-light btn-lg btn-block mb-2" disabled={this.state.loading} onClick={() => this.resetAuthentication('login')}>
          mit anderem Account einloggen
        </button>
        <a href="/usercp/webhosting/order" className="btn btn-primary btn-lg btn-block mb-2">Webhosting-Paket buchen</a>
        <a href="/usercp/tickets/new" className="btn btn-light btn-lg btn-block">Support kontaktieren</a>
      </div>
    );
  }

  render() {
    if (this.state.step === 'signup') return this.renderSignupStep();
    if (this.state.step === 'activation') return this.renderActivationStep();
    if (this.state.step === 'confirm') return this.renderConfirmStep();
    if (this.state.step === 'success') return this.renderSuccessStep();
    if (this.state.step === 'blocked') return this.renderBlockedStep();

    return this.renderEmailStep();
  }
}

WebhostingTrialWizard.defaultProps = {
  initial_offer_code: null,
  hosttest_reviews: null,
  lead: null,
  logged_in: false,
  user_confirmed: false,
  show_captcha: false,
  user_email: null
};

WebhostingTrialWizard.propTypes = {
  activation_url: PropTypes.string.isRequired,
  create_lead_url: PropTypes.string.isRequired,
  hosttest_reviews: PropTypes.shape({
    rating: PropTypes.object,
    reviews: PropTypes.array
  }),
  initial_offer_code: PropTypes.string,
  lead: PropTypes.object,
  logged_in: PropTypes.bool,
  user_confirmed: PropTypes.bool,
  offers: PropTypes.arrayOf(PropTypes.shape({
    offer_code: PropTypes.string.isRequired,
    name: PropTypes.string,
    disk_space_gb: PropTypes.number,
    mysql_space_gb: PropTypes.number,
    email_space_gb: PropTypes.number,
    contract_period: PropTypes.number,
    billing_cycle: PropTypes.number,
    billing_cycle_label: PropTypes.string,
    contract_period_label: PropTypes.string,
    first_interval: PropTypes.bool,
    first_interval_months: PropTypes.number,
    first_interval_price: PropTypes.string,
    first_payment_amount: PropTypes.string,
    first_payment_tax_amount: PropTypes.string,
    gross_price_footnote: PropTypes.string,
    monthly_tax_amount: PropTypes.string,
    price: PropTypes.string,
    regular_price: PropTypes.string,
    setup_price: PropTypes.string,
    setup_price_free: PropTypes.bool
  })).isRequired,
  recaptcha_site_key: PropTypes.string,
  show_captcha: PropTypes.bool,
  sign_out_url: PropTypes.string.isRequired,
  signup_url: PropTypes.string.isRequired,
  user_email: PropTypes.string
};

export default WebhostingTrialWizard;
